import { get } from 'svelte/store';

import type { MenuListDataType } from 'src/components/Profiles/DropdownItem.types';
import type { Home, HomeAvatar } from 'src/types/Home';
import { user_data } from 'src/stores/account/user';
import { app, os } from 'src/stores/session/native-bridge';
import {
	home_switched_android,
	set_homes_data_android,
	set_profiles_data_android,
} from 'src/services/lists_update_android';
import {
	home_switched_ios,
	set_homes_data_ios,
	set_profiles_data_ios,
} from 'src/services/lists_update_ios';

export type NativeProfileAvatar = {
	text?: string;
	image?: string;
	backgroundColor?: string;
};

export type NativeProfileInfo = {
	email: string;
	title: string;
	isSelected: boolean;
	is_sub: boolean;
	installer: boolean;
	sub?: number;
	acc_avatar?: NativeProfileAvatar;
};

export type NativeProfileActiveSession = {
	email?: string;
	installer?: number | boolean;
};

export type NativeProfilesPayload = {
	activeEmail: string;
	profiles: NativeProfileInfo[];
};

export type NativeHome = {
	id: string;
	name: string;
	plan: Home['plan'];
	isActive: boolean;
	isShared: boolean;
	sub?: number;
	avatar?: HomeAvatar;
};

export type NativeHomesPayload = {
	ownerEmail: string;
	homes: NativeHome[];
};

export const shouldSyncToNative = (): boolean => get(app) && !!get(user_data).email;

const cleanAvatarForNative = (
	avatar: { text?: string; image?: string; backgroundColor?: string } | undefined,
): NativeProfileAvatar | undefined => {
	if (!avatar) return undefined;

	const cleaned: NativeProfileAvatar = {};

	if (typeof avatar.text === 'string' && avatar.text.length > 0) {
		cleaned.text = avatar.text;
	}
	if (typeof avatar.image === 'string' && avatar.image.length > 0) {
		cleaned.image = avatar.image;
	}
	if (typeof avatar.backgroundColor === 'string' && avatar.backgroundColor.length > 0) {
		cleaned.backgroundColor = avatar.backgroundColor;
	}

	return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

const cleanHomeAvatarForNative = (avatar: HomeAvatar | undefined): HomeAvatar | undefined => {
	if (!avatar) return undefined;

	const cleaned: HomeAvatar = {};

	if (typeof avatar.text === 'string' && avatar.text.length > 0) {
		cleaned.text = avatar.text;
	}
	if (typeof avatar.image === 'string' && avatar.image.length > 0) {
		cleaned.image = avatar.image;
	}
	if (typeof avatar.backgroundColor === 'string' && avatar.backgroundColor.length > 0) {
		cleaned.backgroundColor = avatar.backgroundColor;
	}

	return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

export const mapProfilesForNative = (
	profiles: MenuListDataType[],
	activeSession?: NativeProfileActiveSession,
): NativeProfilesPayload => {
	const activeProfile = profiles.find((profile) => profile.isSelected);

	return {
		activeEmail: activeProfile?.email ?? '',
		profiles: profiles.map((profile) => {
			const mapped: NativeProfileInfo = {
				email: profile.email ?? '',
				title: profile.title ?? profile.email ?? '',
				isSelected: !!profile.isSelected,
				is_sub: !!profile.is_sub,
				installer:
					!!profile.installer ||
					(!!profile.isSelected &&
						profile.email === activeSession?.email &&
						!!activeSession?.installer),
			};

			if (typeof profile.sub === 'number') {
				mapped.sub = profile.sub;
			}

			const avatar = cleanAvatarForNative(profile.acc_avatar);
			if (avatar) {
				mapped.acc_avatar = avatar;
			}

			return mapped;
		}),
	};
};

export const mapHomesForNative = (homes: Home[]): NativeHome[] =>
	homes.map((home) => {
		const mapped: NativeHome = {
			id: home.id,
			name: home.name,
			plan: home.plan,
			isActive: home.isActive,
			isShared: home.isShared,
		};

		if (typeof home.sub === 'number') {
			mapped.sub = home.sub;
		}

		const avatar = cleanHomeAvatarForNative(home.avatar);
		if (avatar) {
			mapped.avatar = avatar;
		}

		return mapped;
	});

export const notifyNativeProfiles = (payload: NativeProfilesPayload): void => {
	if (!shouldSyncToNative()) return;

	if (get(os) === 'IOS') {
		set_profiles_data_ios(payload);
		return;
	}

	void set_profiles_data_android(payload);
};

export const notifyNativeHomes = (homes: Home[], email?: string): void => {
	if (!shouldSyncToNative()) return;

	const payload = {
		ownerEmail: email ? email : get(user_data)?.email,
		homes: mapHomesForNative(homes),
	};

	if (get(os) === 'IOS') {
		set_homes_data_ios(payload);
		return;
	}

	void set_homes_data_android(payload);
};

export const notifyNativeHomeSwitched = (unitId: string, subId?: string): void => {
	if (!shouldSyncToNative() || !unitId) return;

	if (get(os) === 'IOS') {
		home_switched_ios(unitId, subId);
		return;
	}

	void home_switched_android(unitId, subId);
};
